'use client';

/**
 * # ConfirmDialog
 *
 * **Register**: product (see `PRODUCT.md` → Register and
 * `.claude/skills/impeccable/reference/product.md` → "Components" +
 * `.claude/skills/impeccable/reference/interaction-design.md` →
 * "Confirmation").
 *
 * Modal confirmation prompt for **destructive or irreversible** actions
 * only. For reversible actions (delete a row, archive an event,
 * unfollow), prefer **undo-toast** instead — see the impeccable
 * interaction-design reference for the exact pattern.
 *
 * ## When to use this dialog
 *
 * Use ONLY when the action is one of:
 *
 * - **Truly irreversible**: account deletion, contract void, payment
 *   refund, GDPR data export request.
 * - **High blast radius**: bulk operations affecting >10 entities,
 *   actions visible to other users (publishing, sending).
 * - **Costly to undo**: third-party side effects (Stripe charges,
 *   Resend emails sent), data export requests with quota.
 *
 * Do NOT use for:
 *
 * - Soft-delete a single row → undo-toast (5–10s window)
 * - Archive / unarchive → undo-toast
 * - Cancel a draft → undo-toast or just confirm inline
 * - Discard unsaved changes → soft prompt on unmount, not a dialog
 *
 * ## Product-mode rules followed
 *
 * - Native `<dialog>` semantics via shadcn AlertDialog (focus trap,
 *   Escape to dismiss, restored focus on close).
 * - Coral on the destructive button only. Cancel is `variant="ghost"`.
 * - Plain text body; no icons in the title.
 * - Type-to-confirm pattern (via `confirmationText` prop) reserved for
 *   the highest-stakes actions only (account deletion, org deletion).
 *   Lower-stakes destructive actions get the standard two-button form.
 *
 * ## Anti-patterns
 *
 * - Asking "Are you sure?" for actions a user can trivially undo.
 *   Users develop confirmation-blindness and click through anyway.
 * - Multiple confirmations stacked (e.g. "Are you sure?" → "Really
 *   sure?"). Use type-to-confirm or escalate to email confirmation
 *   instead.
 * - Coral on the cancel button. Cancel never carries weight.
 */
import * as React from 'react';
import { useCallback, useRef, useState } from 'react';

import { cn } from '../lib/utils';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../shadcn/alert-dialog';
import { buttonVariants } from '../shadcn/button';
import { Input } from '../shadcn/input';

/**
 * Props for the ConfirmDialog component
 */
export interface ConfirmDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description: string;
  confirmText?: string;
  cancelText?: string;
  variant?: 'default' | 'destructive';
  onConfirm: () => void;
  onCancel?: () => void;
}

/**
 * A reusable confirmation dialog component
 *
 * @example
 * ```tsx
 * <ConfirmDialog
 *   open={isOpen}
 *   onOpenChange={setIsOpen}
 *   title="Delete item?"
 *   description="This action cannot be undone."
 *   variant="destructive"
 *   onConfirm={() => deleteItem()}
 * />
 * ```
 */
export function ConfirmDialog({
  open,
  onOpenChange,
  title,
  description,
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  variant = 'default',
  onConfirm,
  onCancel,
}: ConfirmDialogProps) {
  const handleCancel = () => {
    onCancel?.();
    onOpenChange(false);
  };

  const handleConfirm = () => {
    onConfirm();
    onOpenChange(false);
  };

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{title}</AlertDialogTitle>
          <AlertDialogDescription>{description}</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel onClick={handleCancel}>
            {cancelText}
          </AlertDialogCancel>
          <AlertDialogAction
            onClick={handleConfirm}
            className={cn(
              variant === 'destructive' &&
                buttonVariants({ variant: 'destructive' }),
            )}
          >
            {confirmText}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

/**
 * Options for the useConfirmDialog hook
 */
export interface UseConfirmDialogOptions {
  title?: string;
  description?: string;
  confirmText?: string;
  cancelText?: string;
  variant?: 'default' | 'destructive';
}

/**
 * State returned by useConfirmDialog hook
 */
export interface ConfirmDialogState {
  isOpen: boolean;
  title: string;
  description: string;
  confirmText: string;
  cancelText: string;
  variant: 'default' | 'destructive';
}

/**
 * A hook that provides a Promise-based API for confirmation dialogs.
 * Replaces window.confirm() with a styled dialog.
 *
 * @example
 * ```tsx
 * function MyComponent() {
 *   const { confirm, ConfirmDialogComponent } = useConfirmDialog();
 *
 *   const handleDelete = async () => {
 *     const confirmed = await confirm({
 *       title: 'Delete item?',
 *       description: 'This action cannot be undone.',
 *       variant: 'destructive',
 *     });
 *
 *     if (confirmed) {
 *       await deleteItem();
 *     }
 *   };
 *
 *   return (
 *     <>
 *       <Button onClick={handleDelete}>Delete</Button>
 *       <ConfirmDialogComponent />
 *     </>
 *   );
 * }
 * ```
 */
export function useConfirmDialog(defaultOptions: UseConfirmDialogOptions = {}) {
  const [state, setState] = useState<ConfirmDialogState>({
    isOpen: false,
    title: defaultOptions.title ?? 'Confirm',
    description: defaultOptions.description ?? 'Are you sure?',
    confirmText: defaultOptions.confirmText ?? 'Confirm',
    cancelText: defaultOptions.cancelText ?? 'Cancel',
    variant: defaultOptions.variant ?? 'default',
  });

  const resolverRef = useRef<((value: boolean) => void) | null>(null);

  const confirm = useCallback(
    (options: UseConfirmDialogOptions = {}): Promise<boolean> => {
      return new Promise((resolve) => {
        resolverRef.current = resolve;
        setState((prev) => ({
          isOpen: true,
          title: options.title ?? prev.title,
          description: options.description ?? prev.description,
          confirmText: options.confirmText ?? prev.confirmText,
          cancelText: options.cancelText ?? prev.cancelText,
          variant: options.variant ?? prev.variant,
        }));
      });
    },
    [],
  );

  const handleOpenChange = useCallback((open: boolean) => {
    if (!open) {
      resolverRef.current?.(false);
      resolverRef.current = null;
    }
    setState((prev) => ({ ...prev, isOpen: open }));
  }, []);

  const handleConfirm = useCallback(() => {
    resolverRef.current?.(true);
    resolverRef.current = null;
    setState((prev) => ({ ...prev, isOpen: false }));
  }, []);

  const handleCancel = useCallback(() => {
    resolverRef.current?.(false);
    resolverRef.current = null;
    setState((prev) => ({ ...prev, isOpen: false }));
  }, []);

  const ConfirmDialogComponent = useCallback(
    () => (
      <ConfirmDialog
        open={state.isOpen}
        onOpenChange={handleOpenChange}
        title={state.title}
        description={state.description}
        confirmText={state.confirmText}
        cancelText={state.cancelText}
        variant={state.variant}
        onConfirm={handleConfirm}
        onCancel={handleCancel}
      />
    ),
    [state, handleOpenChange, handleConfirm, handleCancel],
  );

  return {
    confirm,
    ConfirmDialogComponent,
    isOpen: state.isOpen,
  };
}

/**
 * Props for the PromptDialog component
 */
export interface PromptDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description?: string;
  placeholder?: string;
  defaultValue?: string;
  confirmText?: string;
  cancelText?: string;
  onConfirm: (value: string) => void;
  onCancel?: () => void;
}

/**
 * A reusable prompt dialog component that collects text input
 *
 * @example
 * ```tsx
 * <PromptDialog
 *   open={isOpen}
 *   onOpenChange={setIsOpen}
 *   title="Enter rejection reason"
 *   placeholder="Reason..."
 *   onConfirm={(reason) => rejectItem(reason)}
 * />
 * ```
 */
export function PromptDialog({
  open,
  onOpenChange,
  title,
  description,
  placeholder,
  defaultValue = '',
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  onConfirm,
  onCancel,
}: PromptDialogProps) {
  const [value, setValue] = useState(defaultValue);
  const inputRef = useRef<HTMLInputElement>(null);

  // Reset value when dialog opens
  React.useEffect(() => {
    if (open) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- reset controlled input state on dialog open transition; intentional sync from prop
      setValue(defaultValue);
      // Focus input after dialog animation
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [open, defaultValue]);

  const handleCancel = () => {
    onCancel?.();
    onOpenChange(false);
  };

  const handleConfirm = () => {
    if (value.trim()) {
      onConfirm(value.trim());
      onOpenChange(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && value.trim()) {
      handleConfirm();
    }
  };

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{title}</AlertDialogTitle>
          {description && (
            <AlertDialogDescription>{description}</AlertDialogDescription>
          )}
        </AlertDialogHeader>
        <div className="py-4">
          <Input
            ref={inputRef}
            value={value}
            onChange={(e) => setValue(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={placeholder}
          />
        </div>
        <AlertDialogFooter>
          <AlertDialogCancel onClick={handleCancel}>
            {cancelText}
          </AlertDialogCancel>
          <AlertDialogAction onClick={handleConfirm} disabled={!value.trim()}>
            {confirmText}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

/**
 * Options for the usePromptDialog hook
 */
export interface UsePromptDialogOptions {
  title?: string;
  description?: string;
  placeholder?: string;
  defaultValue?: string;
  confirmText?: string;
  cancelText?: string;
}

/**
 * A hook that provides a Promise-based API for prompt dialogs.
 * Replaces window.prompt() with a styled dialog.
 *
 * @example
 * ```tsx
 * function MyComponent() {
 *   const { prompt, PromptDialogComponent } = usePromptDialog();
 *
 *   const handleReject = async () => {
 *     const reason = await prompt({
 *       title: 'Enter rejection reason',
 *       placeholder: 'Reason...',
 *     });
 *
 *     if (reason) {
 *       await rejectItem(reason);
 *     }
 *   };
 *
 *   return (
 *     <>
 *       <Button onClick={handleReject}>Reject</Button>
 *       <PromptDialogComponent />
 *     </>
 *   );
 * }
 * ```
 */
export function usePromptDialog(defaultOptions: UsePromptDialogOptions = {}) {
  const [state, setState] = useState({
    isOpen: false,
    title: defaultOptions.title ?? 'Enter value',
    description: defaultOptions.description,
    placeholder: defaultOptions.placeholder,
    defaultValue: defaultOptions.defaultValue ?? '',
    confirmText: defaultOptions.confirmText ?? 'Confirm',
    cancelText: defaultOptions.cancelText ?? 'Cancel',
  });

  const resolverRef = useRef<((value: string | null) => void) | null>(null);

  const prompt = useCallback(
    (options: UsePromptDialogOptions = {}): Promise<string | null> => {
      return new Promise((resolve) => {
        resolverRef.current = resolve;
        setState((prev) => ({
          isOpen: true,
          title: options.title ?? prev.title,
          description: options.description ?? prev.description,
          placeholder: options.placeholder ?? prev.placeholder,
          defaultValue: options.defaultValue ?? '',
          confirmText: options.confirmText ?? prev.confirmText,
          cancelText: options.cancelText ?? prev.cancelText,
        }));
      });
    },
    [],
  );

  const handleOpenChange = useCallback((open: boolean) => {
    if (!open) {
      resolverRef.current?.(null);
      resolverRef.current = null;
    }
    setState((prev) => ({ ...prev, isOpen: open }));
  }, []);

  const handleConfirm = useCallback((value: string) => {
    resolverRef.current?.(value);
    resolverRef.current = null;
    setState((prev) => ({ ...prev, isOpen: false }));
  }, []);

  const handleCancel = useCallback(() => {
    resolverRef.current?.(null);
    resolverRef.current = null;
    setState((prev) => ({ ...prev, isOpen: false }));
  }, []);

  const PromptDialogComponent = useCallback(
    () => (
      <PromptDialog
        open={state.isOpen}
        onOpenChange={handleOpenChange}
        title={state.title}
        description={state.description}
        placeholder={state.placeholder}
        defaultValue={state.defaultValue}
        confirmText={state.confirmText}
        cancelText={state.cancelText}
        onConfirm={handleConfirm}
        onCancel={handleCancel}
      />
    ),
    [state, handleOpenChange, handleConfirm, handleCancel],
  );

  return {
    prompt,
    PromptDialogComponent,
    isOpen: state.isOpen,
  };
}
